import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import {
  governedSlugOf,
  governedSlugSpellings,
  parseJsonArrayColumn,
  MODULE_GOVERNANCE_FACT_TYPES,
  type ModuleActionItemRecord,
  type ModuleActivityItem,
  type ModuleAdoptionResult,
  type ModuleAdoptionRow,
  type ModuleAdoptionTotals,
  type ModuleCatalogRecord,
  type ModuleGovernanceFact,
  type ModuleOwnerFormData,
  type ModuleOwnerRecord,
  type ModuleReleaseRecord,
  type ModuleSummaryRecord,
} from "../modules.types";
import { canWriteModules, isModulesAdmin } from "../rbac";
import {
  actionItemOptionLabel,
  buildClosePayload,
  buildReopenPayload,
  closeConfirmation,
  closedSummary,
  isClosedActionItem,
  type ActionItemCloseReason,
} from "../../meetingIntelligence/actionItemClose";
import { subscribeModuleEvents, type ModuleServiceEvent } from "../modulesRealtime";
import type { CommunicationRecord, DevOpsEventRecord } from "../../clients/clients.types";
import type { DevOpsLink } from "../../devops/devops.types";
import {
  dateValue,
  nextSort,
  sortRows,
  textValue,
  type SortDir,
  type SortValue,
} from "components/TableSort/tableSort";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import { isSessionEnded } from "stores/sessionGuard";

/**
 * A Feathers `find` result as the ServiceStores surface it: either the
 * paginated envelope `{ data, total }` or — for the derived find-only services
 * — the bare array of rows. Local to this store, exactly as ClientDetailStore
 * declares its own.
 */
type FindResult<T> = { total?: number; data?: T[] } | T[] | null | undefined;

/** Normalise a {@link FindResult} into a plain array of rows. */
function rowsOf<T>(result: FindResult<T>): T[] {
  if (Array.isArray(result)) return result;
  return result?.data ?? [];
}

/**
 * The RAW Feathers service surface (`rootStore.app.service(path)`).
 *
 * WHY THIS EXISTS — `ServiceStore.create/patch` swallow server errors and
 * resolve `undefined`, so a rejected write reports as a success. Every write on
 * this page is an ownership or ledger change a human must be told about when it
 * fails, so they all go through the raw service. The same trap is documented at
 * `views/billing/Invoices/InvoicesStore.ts:355-399`.
 */
interface RawService {
  create(data: object): Promise<unknown>;
  // `number` as well as `string`: `read-ai-action-items` has an integer id, and
  // the close/reopen writes on the Action Items tab go down this same path.
  patch(id: number | string, data: object): Promise<unknown>;
  remove(id: string): Promise<unknown>;
}

/**
 * One `read_ai_assignments` row as this view reads it. Declared locally rather
 * than in `modules.types.ts` because it is the meeting-intelligence record
 * (`views/meetingIntelligence/meetingIntelligence.types.ts`) seen through the
 * module lens: the API added `governedRepo`/`governedSlug` to it, and those two
 * columns are what attribute a work item to a catalog entry.
 */
export interface ModuleAssignmentRow {
  id: number | string;
  status?: string | null;
  title?: string | null;
  userId?: number | null;
  assigneeName?: string | null;
  assigneeEmail?: string | null;
  governedRepo?: string | null;
  governedSlug?: string | null;
  clientId?: string | null;
  createdAt?: string | null;
}

/** A staff account seen DOING governed work on this entry (vs. assigned to it). */
export interface ModuleObservedContributor {
  /** `users.id`, or null when the assignment names a person with no account. */
  userId: number | null;
  name: string;
  email?: string | null;
  /** Governed work items attributed to this entry for this person. */
  items: number;
  /** Of those, the ones still open (not completed, never rejected). */
  openItems: number;
  /** Newest attributed item — how recently they actually touched the entry. */
  lastAt: string | null;
}

/**
 * A `seam` governance fact unfolded back into the Coordinator's data-flow
 * shape. `module_governance` is a flat fact table, so the sync folds a seam's
 * structured fields into `factKey` (`from->to`), `label`, `status` (the gap
 * status) and `detail`. This is the inverse, written DEFENSIVELY: `detail` is
 * parsed as JSON when it is JSON and treated as prose when it is not, so the
 * Data Flow tab renders whatever shape WP2's planner settled on rather than
 * blanking on an unexpected one.
 */
export interface ModuleSeam {
  factKey: string;
  from: string | null;
  to: string | null;
  label: string | null;
  transport: string | null;
  writer: string | null;
  entity: string | null;
  canonicalHome: string | null;
  /** The gap status — `open` means the handoff is still unbuilt. */
  gapStatus: string | null;
  gapNote: string | null;
  /** True while the seam's gap is anything other than closed/done/none. */
  gapOpen: boolean;
  severity?: string | null;
  evidence?: string | null;
}

const CLOSED_GAP_STATUSES = new Set(["closed", "done", "complete", "completed", "none", "ok"]);

/**
 * Read a `seam` fact back into its structured form. Pure and exported so the
 * Data Flow tab stays a renderer and the parsing is unit-tested.
 */
export function parseSeamFact(fact: ModuleGovernanceFact): ModuleSeam {
  const detail: Record<string, unknown> = (() => {
    if (!fact.detail) return {};
    try {
      const parsed: unknown = JSON.parse(fact.detail);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : {};
    } catch {
      return {};
    }
  })();

  const str = (value: unknown): string | null =>
    typeof value === "string" && value.trim() ? value.trim() : null;

  // `from->to` is the factKey the sync mints (a seam is attributed to BOTH
  // endpoints, same key on each row), but never trust it exclusively.
  const [keyFrom, keyTo] = fact.factKey.includes("->")
    ? fact.factKey.split("->", 2)
    : [null, null];

  const gap = (detail.gap && typeof detail.gap === "object" ? detail.gap : {}) as Record<
    string,
    unknown
  >;
  const gapStatus = str(gap.status) ?? str(fact.status);

  return {
    factKey: fact.factKey,
    from: str(detail.from) ?? str(keyFrom),
    to: str(detail.to) ?? str(keyTo),
    label: str(fact.label) ?? str(detail.handoff),
    transport: str(detail.transport),
    writer: str(detail.writer),
    entity: str(detail.entity),
    canonicalHome: str(detail.canonicalHome),
    gapStatus,
    // Prose `detail` (not JSON) still carries the note — surface it either way.
    gapNote: str(gap.note) ?? (Object.keys(detail).length === 0 ? str(fact.detail) : null),
    gapOpen: !!gapStatus && !CLOSED_GAP_STATUSES.has(gapStatus.toLowerCase()),
    severity: fact.severity ?? null,
    evidence: fact.evidence ?? null,
  };
}

// ---------------------------------------------------------------------------
// Per-tab search / filter / sort vocabulary.
//
// Inactive tabs UNMOUNT, so none of this can live in component state — a tab
// switch would silently reset the operator's filters. Each namespace below
// belongs to exactly one tab and drives a computed over an already-loaded,
// slug-scoped collection through the shared `components/TableSort` helpers.
// ---------------------------------------------------------------------------

/** Sortable columns of the Adoption (customers) table. */
export type AdoptionSortKey = "client" | "source" | "key" | "status" | "since";
const ADOPTION_DESC_FIRST: ReadonlySet<AdoptionSortKey> = new Set<AdoptionSortKey>(["since"]);

/** Sortable columns of the Team (owners) table. */
export type OwnerSortKey = "user" | "role" | "primary" | "startedAt";
const OWNERS_DESC_FIRST: ReadonlySet<OwnerSortKey> = new Set<OwnerSortKey>([
  "primary",
  "startedAt",
]);

/** Sortable columns of the Releases table. */
export type ReleaseSortKey = "package" | "version" | "state" | "observedAt";
const RELEASES_DESC_FIRST: ReadonlySet<ReleaseSortKey> = new Set<ReleaseSortKey>(["observedAt"]);

/** Governance fact-type facet — "all" or one `factType`. */
export type GovernanceFactTypeFilter = "all" | string;

/** Sortable columns of the Action Items table. */
export type ActionItemSortKey =
  | "text"
  | "client"
  | "meeting"
  | "assignee"
  | "priority"
  | "dueDate"
  | "state"
  | "raised";
const ACTION_ITEMS_DESC_FIRST: ReadonlySet<ActionItemSortKey> = new Set<ActionItemSortKey>([
  "dueDate",
  "raised",
]);

/**
 * The independently-loaded sections of the page. Each records its OWN load
 * outcome in {@link ModuleDetailStore.sectionErrors}, so one dead service
 * marks one panel as unavailable instead of painting it "empty" — and never
 * blanks the others. `devopsLinks` / `devopsEvents` are two finds behind one
 * tab, kept apart so each table can say which half failed.
 */
export type ModuleDetailSection =
  | "summary"
  | "adoption"
  | "owners"
  | "assignments"
  | "actionItems"
  | "governance"
  | "releases"
  | "activity"
  | "comms"
  | "devopsLinks"
  | "devopsEvents";

/** Page cap for the slug-scoped child collections. */
export const MODULE_DETAIL_FETCH_LIMIT = 200;

/** One package's version history, newest first (the Releases tab's grouping). */
export interface ModuleReleaseGroup {
  packageName: string;
  releases: ModuleReleaseRecord[];
  latest: ModuleReleaseRecord | null;
}

/**
 * View store for the Module 360 detail hub.
 *
 * Plain class with `makeAutoObservable` — route ViewStores never extend
 * ViewStore and never use decorators. Reads the registered ServiceStores by
 * hand across `module-catalog`, `module-summary`, `module-adoption`,
 * `module-owners`, `module-governance`, `module-releases`, `module-activity`,
 * plus the slug-scoped slices of `communications`, `devops-links`,
 * `devops-events` and `read-ai-assignments`.
 *
 * IDENTITY: the route param is the `governedSlug`. `module-catalog` is keyed by
 * `id`, not by slug, so the entry is resolved with a FIND on
 * `{ moduleKey: slug }` and the single row taken — `moduleKey` IS the slug for
 * a governed row (see {@link governedSlugOf}). A slug with no row is a real,
 * renderable state ({@link notFound}), not a crash: the catalog is a mirror and
 * a link can outlive a retired entry.
 *
 * EVERY child collection is a slug-SCOPED find, re-queried when the open slug
 * changes, so each lives in a local observable populated from the `find()`
 * RESPONSE rather than the cross-query entity cache.
 *
 * `componentOf` (Coordinator CATALOG.md §3): an entry that lives inside another
 * repo has no repo-level facts of its own. The DevOps loaders SKIP the query
 * entirely for a component rather than fetching rows that would be the parent's
 * — and the tabs say so in place of rendering a bare zero. Identity facts
 * (packages, display name, domain role, ownership, adoption) work on components
 * normally.
 *
 * DEGRADATION: no loader rethrows — a dead rollup must not blank the identity
 * the page exists to show — but none of them hides a failure either. Every find
 * goes through `findOrThrow` (the library ServiceStore otherwise SWALLOWS a
 * failed find into `errorOnFind` and resolves null, or an offline store answers
 * from its cache, so a plain try/catch never fires), and the outcome lands in
 * {@link loadError} for the catalog entry itself and in {@link sectionErrors}
 * per child section. A failed section renders an inline error with Retry; its
 * empty sentence is only ever shown after a SUCCESSFUL empty load.
 */
class ModuleDetailStore {
  rootStore: RootStore;

  /** The open `governedSlug` (the `:slug` route param). */
  slug: string | null = null;
  loading = false;
  /** The slug resolved to no catalog row — a real state, not an error. */
  notFound = false;
  /**
   * The catalog-entry find itself FAILED (as opposed to answering "no row",
   * which is {@link notFound}). Cleared at the start of every load.
   */
  loadError: string | null = null;
  /**
   * Per-section load failures, keyed by {@link ModuleDetailSection}. A key is
   * present only while that section's LAST load failed; a successful load —
   * empty or not — deletes it. Load STATE only: the rows live where they did.
   */
  sectionErrors: Partial<Record<ModuleDetailSection, string>> = {};

  entry: ModuleCatalogRecord | null = null;
  summary: ModuleSummaryRecord | null = null;

  // ---- Adoption (customers across the three join tables) ------------------
  adoptionRows: ModuleAdoptionRow[] = [];
  adoptionTotals: ModuleAdoptionTotals | null = null;
  /** The API reported it capped the row set — surfaced, never hidden. */
  adoptionTruncated = false;
  adoptionLoading = false;

  // ---- Team (owners + observed contributors) ------------------------------
  owners: ModuleOwnerRecord[] = [];
  ownersLoading = false;
  ownerForm: ModuleOwnerFormData = {};
  editingOwnerId: string | null = null;
  ownerSaving = false;
  /**
   * Why the owner dialog's last Save was refused. The route throws it, so it
   * shows once, in that dialog's footer — never on the page behind it.
   */
  ownerError: string | null = null;
  /** Why the last row action on the owners table (primary star, Remove) was refused — shown above it. */
  ownerActionError: string | null = null;

  /** Governed work items attributed to this entry (observed contributors). */
  assignments: ModuleAssignmentRow[] = [];
  assignmentsLoading = false;

  // ---- Action Items (meeting-derived work attributed to this entry) -------
  actionItems: ModuleActionItemRecord[] = [];
  actionItemsLoading = false;

  // ---- Governance / releases / activity / comms / devops ------------------
  governance: ModuleGovernanceFact[] = [];
  governanceLoading = false;

  releases: ModuleReleaseRecord[] = [];
  releasesLoading = false;

  activity: ModuleActivityItem[] = [];
  activityLoading = false;
  /** Number of timeline pages currently shown ("Show more" increments). */
  activityPage = 1;

  communications: CommunicationRecord[] = [];
  commsLoading = false;
  noteSubject = "";
  noteSummary = "";
  noteSaving = false;
  noteError: string | null = null;

  devopsLinks: DevOpsLink[] = [];
  devopsEvents: DevOpsEventRecord[] = [];
  devopsLoading = false;
  /** True when the DevOps query was SKIPPED because this entry is a component. */
  devopsSkippedForComponent = false;

  // ---- Per-tab view state (see the vocabulary block above) ----------------
  adoptionSearch = "";
  adoptionSortKey: AdoptionSortKey = "client";
  adoptionSortDir: SortDir = "asc";

  ownersSortKey: OwnerSortKey = "primary";
  ownersSortDir: SortDir = "desc";

  releasesSearch = "";
  releasesSortKey: ReleaseSortKey = "observedAt";
  releasesSortDir: SortDir = "desc";

  governanceSearch = "";
  governanceFactTypeFilter: GovernanceFactTypeFilter = "all";

  actionItemsSearch = "";
  // Newest first, matching the order the API already returns (`ai.createdAt`
  // DESC) — the default view answers "what came up most recently".
  actionItemsSortKey: ActionItemSortKey = "raised";
  actionItemsSortDir: SortDir = "desc";
  /**
   * Whether items a human CLOSED (with a reason) are listed alongside the open
   * ones. Off by default: the tab answers "what work is attributed here", and a
   * settled item is not outstanding work. See
   * `views/meetingIntelligence/actionItemClose.ts`.
   */
  actionItemsShowClosed = false;

  // ---- Close-with-reason dialog (Action Items tab; operator+, raw service) --
  /** The item the close dialog is about, or null. Per-item only here: the bulk
   *  close is one meeting per call, and this tab spans meetings. */
  closingActionItemId: number | null = null;
  closeReason: ActionItemCloseReason = "done";
  closeEvidence = "";
  closeDuplicateOfItemId = "";
  closingActionItem = false;
  /**
   * Why the close dialog's last Close was refused. The route throws it, so it
   * shows once, in that dialog's footer — never on the tab behind it.
   */
  actionItemCloseError: string | null = null;
  /** Why the last row Reopen was refused — shown above the list. */
  actionItemReopenError: string | null = null;
  /** Item id whose reopen is in flight, or null. */
  reopeningActionItemId: number | null = null;

  // Realtime subscription disposer (plain plumbing — not observable).
  private realtimeDisposer: (() => void) | null = null;
  /** Monotonic guard so a stale entry find can never clobber a newer load. */
  private loadSeq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ---- Registered ServiceStore handles ------------------------------------
  // Two-tier lookup: the ServiceStoreManager registry first, then the mapped
  // `rootStore.<camel>Store`, matching ClientDetailStore.

  /** The EXISTING `module-catalog` store (serviceStores/clients.stores.ts). */
  private get moduleCatalogService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("module-catalog") ??
        this.rootStore.moduleCatalogStore) as unknown as ServiceStoreType | null) ?? null
    );
  }
  private get moduleSummaryService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("module-summary") ??
        this.rootStore.moduleSummaryStore) as unknown as ServiceStoreType | null) ?? null
    );
  }
  private get moduleAdoptionService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("module-adoption") ??
        this.rootStore.moduleAdoptionStore) as unknown as ServiceStoreType | null) ?? null
    );
  }
  private get moduleOwnersService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("module-owners") ??
        this.rootStore.moduleOwnersStore) as unknown as ServiceStoreType | null) ?? null
    );
  }
  private get moduleGovernanceService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("module-governance") ??
        this.rootStore.moduleGovernanceStore) as unknown as ServiceStoreType | null) ?? null
    );
  }
  private get moduleReleasesService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("module-releases") ??
        this.rootStore.moduleReleasesStore) as unknown as ServiceStoreType | null) ?? null
    );
  }
  private get moduleActivityService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("module-activity") ??
        this.rootStore.moduleActivityStore) as unknown as ServiceStoreType | null) ?? null
    );
  }
  private get communicationsService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("communications") ??
        this.rootStore.communicationsStore) as unknown as ServiceStoreType | null) ?? null
    );
  }
  private get devopsLinksService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("devops-links") ??
        this.rootStore.devopsLinksStore) as unknown as ServiceStoreType | null) ?? null
    );
  }
  private get devopsEventsService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("devops-events") ??
        this.rootStore.devopsEventsStore) as unknown as ServiceStoreType | null) ?? null
    );
  }
  /** `read-ai-assignments` — governed work items (observed contributors). */
  private get assignmentsService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("read-ai-assignments") ??
        this.rootStore.readAiAssignmentsStore) as unknown as ServiceStoreType | null) ?? null
    );
  }
  /** `read-ai-action-items` — the meeting-derived work itself (find/get only). */
  private get actionItemsService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("read-ai-action-items") ??
        this.rootStore.readAiActionItemsStore) as unknown as ServiceStoreType | null) ?? null
    );
  }
  /** The builtin `user` ServiceStore — the id → name directory for owners. */
  private get usersService(): ServiceStoreType | null {
    return (this.rootStore.userStore as ServiceStoreType | null) ?? null;
  }

  /**
   * The raw Feathers service for a path. Used by every write on this page —
   * see the {@link RawService} docblock for why the ServiceStore is not.
   */
  private rawService(path: string): RawService | null {
    const app = this.rootStore.app as unknown as
      | { service?: (p: string) => RawService }
      | undefined;
    return app?.service?.(path) ?? null;
  }

  // ---- Identity -----------------------------------------------------------

  setSlug(slug: string) {
    if (this.slug !== slug) {
      // A different entry — drop the previous one's rows so a slow child load
      // can never paint the old entry's data under the new entry's header.
      this.resetForSlug();
    }
    this.slug = slug;
    this.subscribeRealtime();
  }

  private resetForSlug() {
    this.entry = null;
    this.summary = null;
    this.notFound = false;
    this.adoptionRows = [];
    this.adoptionTotals = null;
    this.adoptionTruncated = false;
    this.owners = [];
    this.assignments = [];
    this.governance = [];
    this.releases = [];
    this.activity = [];
    this.activityPage = 1;
    this.actionItems = [];
    this.loadError = null;
    this.sectionErrors = {};
    this.actionItemsShowClosed = false;
    this.resetActionItemCloseForm();
    this.communications = [];
    this.devopsLinks = [];
    this.devopsEvents = [];
    this.devopsSkippedForComponent = false;
    this.ownerError = null;
    this.ownerActionError = null;
    this.actionItemReopenError = null;
    this.noteError = null;
  }

  // ---- Load outcome -------------------------------------------------------

  /** The last load failure of one section, or null when it last loaded fine. */
  sectionError(key: ModuleDetailSection): string | null {
    return this.sectionErrors[key] ?? null;
  }

  /**
   * The message of the last FAILED action-items load, or null when the last
   * load succeeded. What lets the tab tell "this entry has no action items"
   * apart from "the load failed" — the two look identical in an empty array.
   */
  get actionItemsError(): string | null {
    return this.sectionError("actionItems");
  }

  private setSectionError(key: ModuleDetailSection, message: string | null) {
    if (message === null) {
      if (!(key in this.sectionErrors)) return;
      const next = { ...this.sectionErrors };
      delete next[key];
      this.sectionErrors = next;
    } else {
      this.sectionErrors = { ...this.sectionErrors, [key]: message };
    }
  }

  /**
   * Run ONE section's find and record its outcome. Rows are applied only on
   * success; a failure keeps whatever the last successful load showed and sets
   * the section's error. Both are dropped if the open slug changed meanwhile, so
   * a slow load for the previous entry can never mark the new one as failed.
   */
  private async runSection<T>(
    key: ModuleDetailSection,
    label: string,
    load: () => Promise<T>,
    apply: (result: T) => void,
  ): Promise<void> {
    const slug = this.slug;
    try {
      const result = await load();
      if (this.slug !== slug) return;
      runInAction(() => {
        apply(result);
        this.setSectionError(key, null);
      });
    } catch (err: unknown) {
      if (this.slug !== slug) return;
      console.error(`Failed to load ${label}:`, err);
      if (isSessionEnded(err)) return;
      runInAction(() => {
        this.setSectionError(key, loadErrorMessage(err, `The ${label} could not be loaded.`));
      });
    }
  }

  /** Re-run the loader behind one section (the inline Retry). */
  retrySection(key: ModuleDetailSection): Promise<void> {
    // Clear first so the section shows its spinner, not the stale error, while
    // the retry is in flight; the load records the fresh outcome either way.
    this.setSectionError(key, null);
    switch (key) {
      case "summary":
        return this.loadSummary();
      case "adoption":
        return this.loadAdoption();
      case "owners":
        return this.loadOwners();
      case "assignments":
        return this.loadAssignments();
      case "actionItems":
        return this.loadActionItems();
      case "governance":
        return this.loadGovernance();
      case "releases":
        return this.loadReleases();
      case "activity":
        return this.loadActivity();
      case "comms":
        return this.loadComms();
      case "devopsLinks":
      case "devopsEvents":
        return this.loadDevOps();
    }
  }

  /** operator+ — whether the write controls render across the tabs. */
  get canWrite(): boolean {
    return canWriteModules(this.rootStore);
  }

  /** admin only — restore / includeDeleted reads. */
  get isAdminTier(): boolean {
    return isModulesAdmin(this.rootStore);
  }

  get displayName(): string {
    return this.entry?.displayName || this.slug || "";
  }

  get governedRepo(): string | null {
    return this.entry?.governedRepo?.trim() || null;
  }

  /** Non-null ⇒ this entry lives inside another repo; repo facts are ITS parent's. */
  get componentOf(): string | null {
    return this.entry?.componentOf?.trim() || null;
  }

  get isComponent(): boolean {
    return !!this.componentOf;
  }

  /** THE GATE — may a client be assigned this entry? */
  get isAssignable(): boolean {
    // Only an explicit false excludes: a web build ahead of the API migration
    // must not report every entry as unassignable.
    return this.entry?.assignable !== false;
  }

  get packages(): string[] {
    return parseJsonArrayColumn(this.entry?.packages);
  }

  get aliases(): string[] {
    return parseJsonArrayColumn(this.entry?.aliases);
  }

  get legacyAliases(): string[] {
    return parseJsonArrayColumn(this.entry?.legacyAliases);
  }

  /**
   * Is `configureSymbol` a VERIFIED fact or a guessed convention?
   *
   * `registry` means the Coordinator read the symbol out of the repo's own
   * registry. `derived` means it was inferred from the slug — and the
   * Coordinator's docs/CATALOG.md records that the convention does not hold for
   * every entry. A consumer that imports a derived symbol as though it were
   * verified imports something that may not exist, so the two are rendered
   * differently everywhere they appear.
   */
  get configureSymbolVerified(): boolean {
    return this.entry?.configureSymbolSource === "registry";
  }

  /**
   * Whole days between the catalog day-stamp this row was mirrored from and
   * today. Prefers the API's own `staleDays` and falls back to the local
   * computation so the chip still works before the rollup lands.
   */
  get mirrorStaleDays(): number | null {
    if (typeof this.summary?.staleDays === "number") return this.summary.staleDays;
    const stamp = dateValue(this.entry?.catalogUpdated ?? this.summary?.catalogUpdated ?? null);
    if (stamp === null) return null;
    return Math.max(0, Math.floor((Date.now() - stamp) / 86_400_000));
  }

  get catalogUpdated(): string | null {
    return this.entry?.catalogUpdated ?? this.summary?.catalogUpdated ?? null;
  }

  // ---- Adoption -----------------------------------------------------------

  get filteredAdoption(): ModuleAdoptionRow[] {
    const q = this.adoptionSearch.trim().toLowerCase();
    if (!q) return this.adoptionRows;
    return this.adoptionRows.filter((row) =>
      [row.clientName, row.rawKey, row.sourceTable, row.status].some(
        (value) => !!value && value.toLowerCase().includes(q),
      ),
    );
  }

  get sortedAdoption(): ModuleAdoptionRow[] {
    const key = this.adoptionSortKey;
    const valueOf = (row: ModuleAdoptionRow): SortValue => {
      switch (key) {
        case "source":
          return textValue(row.sourceTable);
        case "key":
          return textValue(row.rawKey);
        case "status":
          return textValue(row.status);
        case "since":
          return dateValue(row.since);
        case "client":
        default:
          return textValue(row.clientName ?? row.clientId);
      }
    };
    return sortRows(this.filteredAdoption, valueOf, this.adoptionSortDir);
  }

  /** Adoption row counts per join table — WHY a client counts, at a glance. */
  get adoptionBySource(): Record<string, number> {
    const counts: Record<string, number> = {};
    for (const row of this.adoptionRows) {
      counts[row.sourceTable] = (counts[row.sourceTable] ?? 0) + 1;
    }
    return counts;
  }

  setAdoptionSearch(q: string) {
    this.adoptionSearch = q;
  }

  setAdoptionSort(key: AdoptionSortKey) {
    const next = nextSort(this.adoptionSortKey, this.adoptionSortDir, key, ADOPTION_DESC_FIRST);
    this.adoptionSortKey = next.key;
    this.adoptionSortDir = next.dir;
  }

  // ---- Team ---------------------------------------------------------------

  /** Live (non soft-deleted) owner rows. */
  get liveOwners(): ModuleOwnerRecord[] {
    return this.owners.filter((owner) => !owner.deletedAt);
  }

  get sortedOwners(): ModuleOwnerRecord[] {
    const key = this.ownersSortKey;
    const valueOf = (owner: ModuleOwnerRecord): SortValue => {
      switch (key) {
        case "role":
          return textValue(owner.role);
        case "primary":
          return owner.isPrimary ? 1 : 0;
        case "startedAt":
          return dateValue(owner.startedAt);
        case "user":
        default:
          return textValue(this.userName(owner.userId));
      }
    };
    return sortRows(this.liveOwners, valueOf, this.ownersSortDir);
  }

  setOwnersSort(key: OwnerSortKey) {
    const next = nextSort(this.ownersSortKey, this.ownersSortDir, key, OWNERS_DESC_FIRST);
    this.ownersSortKey = next.key;
    this.ownersSortDir = next.dir;
  }

  get primaryOwner(): ModuleOwnerRecord | null {
    return this.liveOwners.find((owner) => owner.isPrimary) ?? null;
  }

  /** The `user` directory (computed view over the builtin store's cache). */
  get users(): Array<{ id: number; name?: string | null; email?: string | null }> {
    return (this.usersService?.entities ?? []) as unknown as Array<{
      id: number;
      name?: string | null;
      email?: string | null;
    }>;
  }

  /** Best available label for a `users.id` — falls back to `#id`, never blank. */
  userName(userId?: number | null): string {
    if (userId == null) return "Unassigned";
    const user = this.users.find((candidate) => Number(candidate.id) === Number(userId));
    return user?.name || user?.email || `#${userId}`;
  }

  /**
   * Staff seen doing governed work on this entry, newest-touch first.
   *
   * Derived from the `read_ai_assignments` slice of the activity feed: an
   * distributed meeting action item is the only per-PERSON signal in the merged
   * timeline (audit rows carry a userId but describe an edit, not ownership).
   * `rejected` items are discarded outright — they were never real work.
   */
  get observedContributors(): ModuleObservedContributor[] {
    const byKey = new Map<string, ModuleObservedContributor>();
    for (const item of this.assignments) {
      if (item.status === "rejected") continue;
      const userId = item.userId ?? null;
      const key = userId != null ? `u${userId}` : `n${item.assigneeName || item.assigneeEmail || "?"}`;
      const existing = byKey.get(key);
      const contributor: ModuleObservedContributor = existing ?? {
        userId,
        name: userId != null ? this.userName(userId) : item.assigneeName || item.assigneeEmail || "Unknown",
        email: item.assigneeEmail ?? null,
        items: 0,
        openItems: 0,
        lastAt: null,
      };
      contributor.items += 1;
      if (item.status !== "completed") contributor.openItems += 1;
      const at = item.createdAt ?? null;
      if (at && (!contributor.lastAt || at > contributor.lastAt)) contributor.lastAt = at;
      byKey.set(key, contributor);
    }
    return [...byKey.values()].sort((a, b) => b.items - a.items);
  }

  /** Governed work items still open across all contributors. */
  get openAssignmentCount(): number {
    return this.assignments.filter(
      (item) => item.status !== "rejected" && item.status !== "completed",
    ).length;
  }

  /**
   * Owners with a role on this entry and NO observed work against it. Not an
   * accusation — a maintainer of a stable module legitimately shows nothing.
   * It is the question worth asking, surfaced instead of buried.
   */
  get assignedButInactive(): ModuleOwnerRecord[] {
    const active = new Set(
      this.observedContributors
        .filter((c) => c.userId != null)
        .map((c) => Number(c.userId)),
    );
    return this.liveOwners.filter((owner) => !active.has(Number(owner.userId)));
  }

  /** People doing governed work on this entry who hold no role on it. */
  get activeButUnassigned(): ModuleObservedContributor[] {
    const assigned = new Set(this.liveOwners.map((owner) => Number(owner.userId)));
    return this.observedContributors.filter(
      (c) => c.userId == null || !assigned.has(Number(c.userId)),
    );
  }

  // ---- Governance ---------------------------------------------------------

  /** Fact types actually present, in the canonical Coordinator order. */
  get governanceFactTypes(): string[] {
    const present = new Set<string>(this.governance.map((fact) => fact.factType));
    // Widened to string[]: `factType` is a free NVARCHAR at the API, so the
    // canonical list is an ORDER, not an exhaustive enum.
    const ordered: string[] = MODULE_GOVERNANCE_FACT_TYPES.map(
      (meta) => meta.key as string,
    ).filter((key) => present.has(key));
    // Any factType the Coordinator adds later still renders, just at the end.
    const extras = [...present].filter((key) => !ordered.includes(key)).sort();
    return [...ordered, ...extras];
  }

  get filteredGovernance(): ModuleGovernanceFact[] {
    let facts = this.governance;
    if (this.governanceFactTypeFilter !== "all") {
      facts = facts.filter((fact) => fact.factType === this.governanceFactTypeFilter);
    }
    const q = this.governanceSearch.trim().toLowerCase();
    if (q) {
      facts = facts.filter((fact) =>
        [fact.factKey, fact.label, fact.status, fact.detail, fact.evidence].some(
          (value) => !!value && value.toLowerCase().includes(q),
        ),
      );
    }
    return facts;
  }

  /** The filtered facts grouped by factType, in {@link governanceFactTypes} order. */
  get governanceByType(): Array<{ factType: string; facts: ModuleGovernanceFact[] }> {
    const groups = new Map<string, ModuleGovernanceFact[]>();
    for (const fact of this.filteredGovernance) {
      const list = groups.get(fact.factType) ?? [];
      list.push(fact);
      groups.set(fact.factType, list);
    }
    return this.governanceFactTypes
      .filter((factType) => groups.has(factType))
      .map((factType) => ({
        factType,
        facts: (groups.get(factType) ?? [])
          .slice()
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
      }));
  }

  /** Counts by severity across ALL loaded facts (never the filtered subset). */
  get governanceCounts(): { total: number; error: number; warn: number; info: number } {
    const counts = { total: this.governance.length, error: 0, warn: 0, info: 0 };
    for (const fact of this.governance) {
      if (fact.severity === "error") counts.error += 1;
      else if (fact.severity === "warn") counts.warn += 1;
      else counts.info += 1;
    }
    return counts;
  }

  /** The `seam` facts, unfolded (the Data Flow tab). */
  get seams(): ModuleSeam[] {
    return this.governance
      .filter((fact) => fact.factType === "seam")
      .map(parseSeamFact)
      .sort((a, b) => Number(b.gapOpen) - Number(a.gapOpen));
  }

  /** The `thread` facts — the cross-module narratives a seam belongs to. */
  get threads(): ModuleGovernanceFact[] {
    return this.governance.filter((fact) => fact.factType === "thread");
  }

  get openSeamCount(): number {
    return this.seams.filter((seam) => seam.gapOpen).length;
  }

  setGovernanceSearch(q: string) {
    this.governanceSearch = q;
  }

  setGovernanceFactTypeFilter(filter: GovernanceFactTypeFilter) {
    this.governanceFactTypeFilter = filter;
  }

  // ---- Releases -----------------------------------------------------------

  get filteredReleases(): ModuleReleaseRecord[] {
    const q = this.releasesSearch.trim().toLowerCase();
    if (!q) return this.releases;
    return this.releases.filter((release) =>
      [release.packageName, release.version, release.state, release.source].some(
        (value) => !!value && value.toLowerCase().includes(q),
      ),
    );
  }

  get sortedReleases(): ModuleReleaseRecord[] {
    const key = this.releasesSortKey;
    const valueOf = (release: ModuleReleaseRecord): SortValue => {
      switch (key) {
        case "package":
          return textValue(release.packageName);
        case "version":
          return textValue(release.version);
        case "state":
          return textValue(release.state);
        case "observedAt":
        default:
          return dateValue(release.observedAt);
      }
    };
    return sortRows(this.filteredReleases, valueOf, this.releasesSortDir);
  }

  /** Version history per package, newest observation first within each group. */
  get releasesByPackage(): ModuleReleaseGroup[] {
    const groups = new Map<string, ModuleReleaseRecord[]>();
    for (const release of this.filteredReleases) {
      const list = groups.get(release.packageName) ?? [];
      list.push(release);
      groups.set(release.packageName, list);
    }
    return [...groups.entries()]
      .sort((a, b) => a[0].localeCompare(b[0], undefined, { sensitivity: "base" }))
      .map(([packageName, rows]) => {
        const releases = sortRows(rows, (r) => dateValue(r.observedAt), "desc");
        return { packageName, releases, latest: releases[0] ?? null };
      });
  }

  setReleasesSearch(q: string) {
    this.releasesSearch = q;
  }

  setReleasesSort(key: ReleaseSortKey) {
    const next = nextSort(this.releasesSortKey, this.releasesSortDir, key, RELEASES_DESC_FIRST);
    this.releasesSortKey = next.key;
    this.releasesSortDir = next.dir;
  }

  // ---- Activity -----------------------------------------------------------

  /** Rows per timeline page ("Show more" reveals another page). */
  static readonly ACTIVITY_PAGE_SIZE = 25;

  /** The merged timeline the API already sorted newest-first (re-sorted defensively). */
  get activityItems(): ModuleActivityItem[] {
    return this.activity
      .slice()
      .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  }

  get pagedActivityItems(): ModuleActivityItem[] {
    return this.activityItems.slice(0, this.activityPage * ModuleDetailStore.ACTIVITY_PAGE_SIZE);
  }

  get hasMoreActivity(): boolean {
    return this.pagedActivityItems.length < this.activityItems.length;
  }

  showMoreActivity() {
    this.activityPage += 1;
  }

  // ---- Action Items -------------------------------------------------------

  /**
   * Every spelling this entry has been known by — the slug plus its live
   * `aliases` and its retired `legacyAliases`. This is the query the tab sends,
   * NOT the bare slug: an item extracted before a rename carries the OLD slug
   * forever, and asking only for the current one silently under-reports.
   *
   * Empty until the catalog entry resolves — the loader treats that as "do not
   * query yet", never as "match everything".
   */
  get actionItemSpellings(): string[] {
    return this.entry ? governedSlugSpellings(this.entry) : [];
  }

  /** Spellings other than the primary slug that actually returned rows. */
  get actionItemAliasHits(): string[] {
    const primary = this.slug;
    const hits = new Set<string>();
    for (const item of this.actionItems) {
      const slug = item.governedSlug ?? null;
      if (slug && slug !== primary) hits.add(slug);
    }
    return [...hits].sort();
  }

  /** Best label for an item's staff assignee, falling back to the spoken name. */
  actionItemAssignee(item: ModuleActionItemRecord): string {
    const resolved = [item.resolvedUserFirstName, item.resolvedUserLastName]
      .filter((part): part is string => !!part && !!part.trim())
      .join(" ")
      .trim();
    if (resolved) return resolved;
    if (item.resolvedUserId != null) return this.userName(item.resolvedUserId);
    return item.assigneeName || item.assigneeEmail || "Unassigned";
  }

  /** The items that are still WORK — everything a count of outstanding work
   *  must be taken over. A closed item has been settled with a reason. */
  get openActionItems(): ModuleActionItemRecord[] {
    return this.actionItems.filter((item) => !isClosedActionItem(item));
  }

  get closedActionItems(): ModuleActionItemRecord[] {
    return this.actionItems.filter((item) => isClosedActionItem(item));
  }

  get filteredActionItems(): ModuleActionItemRecord[] {
    // Closed items are excluded FIRST, so the search box never surfaces one
    // while the toggle is off.
    const base = this.actionItemsShowClosed
      ? this.actionItems
      : this.openActionItems;
    const q = this.actionItemsSearch.trim().toLowerCase();
    if (!q) return base;
    return base.filter((item) =>
      [
        item.text,
        item.context,
        item.clientName,
        item.clientCode,
        item.meetingTitle,
        item.assigneeName,
        item.assigneeEmail,
        item.distributionStatus,
        item.governedSlug,
        this.actionItemAssignee(item),
      ]
        .filter((value): value is string => typeof value === "string")
        .some((value) => value.toLowerCase().includes(q)),
    );
  }

  get sortedActionItems(): ModuleActionItemRecord[] {
    const key = this.actionItemsSortKey;
    const valueOf = (item: ModuleActionItemRecord): SortValue => {
      switch (key) {
        case "client":
          return textValue(item.clientName || item.clientCode);
        case "meeting":
          return textValue(item.meetingTitle);
        case "assignee":
          return textValue(this.actionItemAssignee(item));
        case "priority":
          return textValue(item.priority);
        case "dueDate":
          return dateValue(item.dueDate);
        case "state":
          return textValue(item.distributionStatus);
        case "raised":
          return dateValue(item.createdAt);
        case "text":
        default:
          return textValue(item.text);
      }
    };
    return sortRows(this.filteredActionItems, valueOf, this.actionItemsSortDir);
  }

  /**
   * Counts for the tab's summary chips.
   *
   * EVERY COUNT OF OUTSTANDING WORK IS TAKEN OVER THE OPEN ITEMS — `queued`,
   * `unattributedClient`, `clients` and `meetings` all answer "what is still
   * outstanding here", and a closed item is not. `total` stays the honest
   * number of rows this entry has ever been attributed, with `open` and
   * `closed` splitting it, so the chips can say so rather than quietly
   * disagreeing with the row count.
   */
  get actionItemCounts(): {
    total: number;
    open: number;
    closed: number;
    queued: number;
    unattributedClient: number;
    clients: number;
    meetings: number;
  } {
    const clients = new Set<string>();
    const meetings = new Set<number>();
    let queued = 0;
    let unattributedClient = 0;
    const open = this.openActionItems;
    for (const item of open) {
      // `assignmentId` non-null is the only in-record evidence that an item
      // reached the review queue — there is no separate flag to invent.
      if (item.assignmentId != null) queued += 1;
      if (item.clientId) clients.add(item.clientId);
      else unattributedClient += 1;
      if (item.meetingId != null) meetings.add(item.meetingId);
    }
    return {
      total: this.actionItems.length,
      open: open.length,
      closed: this.actionItems.length - open.length,
      queued,
      unattributedClient,
      clients: clients.size,
      meetings: meetings.size,
    };
  }

  setActionItemsSearch(q: string) {
    this.actionItemsSearch = q;
  }

  setActionItemsSort(key: ActionItemSortKey) {
    const next = nextSort(
      this.actionItemsSortKey,
      this.actionItemsSortDir,
      key,
      ACTION_ITEMS_DESC_FIRST,
    );
    this.actionItemsSortKey = next.key;
    this.actionItemsSortDir = next.dir;
  }

  setActionItemsShowClosed(show: boolean) {
    this.actionItemsShowClosed = show;
  }

  // ---- Close an action item with a reason (operator+, raw service) ---------

  /**
   * Open the close dialog for one item.
   *
   * PER ITEM ONLY on this tab, deliberately: the bulk close is scoped to ONE
   * MEETING per call and Module 360 lists items from many meetings at once, so
   * a "close all" here would fan out into N calls with no all-or-nothing
   * guarantee. The bulk path lives on Meeting Detail, where the scope matches.
   */
  startCloseActionItem(id: number) {
    this.resetActionItemCloseForm();
    this.closingActionItemId = id;
    this.rootStore.routerStore?.openDialog?.({
      dialogName: "ModuleCloseActionItemDialog",
    });
  }

  private resetActionItemCloseForm() {
    this.closingActionItemId = null;
    this.closeReason = "done";
    this.closeEvidence = "";
    this.closeDuplicateOfItemId = "";
    this.actionItemCloseError = null;
  }

  setCloseReason(reason: ActionItemCloseReason) {
    this.closeReason = reason;
  }

  setCloseEvidence(value: string) {
    this.closeEvidence = value;
  }

  setCloseDuplicateOfItemId(value: string) {
    this.closeDuplicateOfItemId = value;
  }

  get isActionItemCloseFormValid(): boolean {
    return this.closingActionItemId != null && !!this.closeReason;
  }

  /** "Close 1 item as already done?" — what the dialog asks. */
  get closeConfirmationText(): string {
    return closeConfirmation(
      this.closingActionItemId == null ? 0 : 1,
      this.closeReason,
    );
  }

  /** Candidates for the "duplicate of" reference — the other items on screen. */
  get duplicateOptions(): { id: number; label: string }[] {
    return this.actionItems
      .filter((item) => item.id !== this.closingActionItemId)
      .map((item) => ({ id: item.id, label: actionItemOptionLabel(item) }));
  }

  /** "Already done · Alice Smith · 17/09/2026" for a closed row. */
  closedSummaryFor(item: ModuleActionItemRecord): string {
    return closedSummary(
      item,
      item.closedBy == null ? null : this.userName(item.closedBy),
    );
  }

  /**
   * Close the item the dialog is about — `PATCH /read-ai-action-items/:id` with
   * `{ action: 'close', reason, … }`. Raw service, for the reason every other
   * write on this page uses one: a refused close that resolves undefined would
   * paint the item as settled when the server never touched it.
   *
   * Creates NO Azure DevOps work item, which is what makes this the right verb
   * for work somebody already did — a developer ACCEPTING it would create one.
   */
  async closeActionItem(): Promise<boolean> {
    const id = this.closingActionItemId;
    if (id == null || this.closingActionItem) return false;
    // The getter is the render gate; this is the write gate. The API
    // re-enforces the operator tier regardless of either.
    if (!this.canWrite) {
      runInAction(() => {
        this.actionItemCloseError =
          "Closing an action item needs the operator role. Ask an administrator to change your role.";
      });
      return false;
    }
    runInAction(() => {
      this.closingActionItem = true;
      this.actionItemCloseError = null;
    });
    const previous = this.applyActionItemFields(id, {
      status: "closed",
      closedReason: this.closeReason,
      closedAt: new Date().toISOString(),
    });
    try {
      const service = this.rawService("read-ai-action-items");
      if (!service) throw new Error("Not connected to the API — reload and try again.");
      const duplicateOfItemId = Number(this.closeDuplicateOfItemId);
      await service.patch(
        id,
        buildClosePayload({
          reason: this.closeReason,
          evidence: this.closeEvidence,
          duplicateOfItemId: Number.isFinite(duplicateOfItemId)
            ? duplicateOfItemId
            : null,
        }),
      );
      await this.loadActionItems();
      runInAction(() => {
        this.resetActionItemCloseForm();
      });
      return true;
    } catch (err: unknown) {
      // A sign-out ended the call: reset() has emptied the list, and `previous`
      // is the signed-out person's rows. Restoring them would hand them to
      // whoever signs in next, so this check comes before the rollback.
      if (isSessionEnded(err)) return false;
      // Put the row back: an item that still reads as closed after a refusal is
      // worse than one that was never closed.
      runInAction(() => {
        this.restoreActionItems(previous);
        this.actionItemCloseError =
          err instanceof Error && err.message
            ? err.message
            : "Failed to close the action item";
      });
      console.error("Failed to close the action item:", err);
      return false;
    } finally {
      runInAction(() => {
        this.closingActionItem = false;
      });
    }
  }

  /** Put a closed item back to open; the API clears every close field. */
  async reopenActionItem(id: number): Promise<boolean> {
    if (!this.canWrite || this.reopeningActionItemId != null) return false;
    runInAction(() => {
      this.reopeningActionItemId = id;
      this.actionItemReopenError = null;
    });
    const previous = this.applyActionItemFields(id, {
      status: "open",
      closedReason: null,
      closedAt: null,
      closedBy: null,
      closedEvidence: null,
      duplicateOfItemId: null,
    });
    try {
      const service = this.rawService("read-ai-action-items");
      if (!service) throw new Error("Not connected to the API — reload and try again.");
      await service.patch(id, buildReopenPayload());
      await this.loadActionItems();
      return true;
    } catch (err: unknown) {
      // Before the rollback, as in closeActionItem: `previous` is the
      // signed-out person's rows.
      if (isSessionEnded(err)) return false;
      runInAction(() => {
        this.restoreActionItems(previous);
        this.actionItemReopenError =
          err instanceof Error && err.message
            ? err.message
            : "Failed to reopen the action item";
      });
      console.error("Failed to reopen the action item:", err);
      return false;
    } finally {
      runInAction(() => {
        this.reopeningActionItemId = null;
      });
    }
  }

  /** Paint the change before the server answers; returns the rows to restore. */
  private applyActionItemFields(
    id: number,
    fields: Partial<ModuleActionItemRecord>,
  ): ModuleActionItemRecord[] {
    const previous = this.actionItems.slice();
    this.actionItems = this.actionItems.map((item) =>
      item.id === id ? { ...item, ...fields } : item,
    );
    return previous;
  }

  private restoreActionItems(previous: ModuleActionItemRecord[]) {
    this.actionItems = previous;
  }

  // ---- Loading ------------------------------------------------------------

  /**
   * Route entry point. Resolves the catalog entry FIRST (the child loads need
   * `componentOf` to know whether repo-level facts apply), then fans the child
   * collections out un-awaited so one slow service never gates the page.
   *
   * A FAILED catalog find sets {@link loadError} and fans nothing out — without
   * the entry there is no `componentOf`/`governedRepo` to scope the children
   * by. It never sets {@link notFound}: "the request failed" and "there is no
   * such entry" are different answers and the page says which.
   */
  async loadModule() {
    if (!this.slug) return;
    const seq = ++this.loadSeq;
    const isInitialLoad = !this.entry || governedSlugOf(this.entry) !== this.slug;
    runInAction(() => {
      if (isInitialLoad) this.loading = true;
      this.loadError = null;
    });
    try {
      // `module-catalog` is keyed by `id`; the slug lives in `moduleKey`, so
      // this is a find-and-take-one, not a get.
      const result = await findOrThrow(this.moduleCatalogService, {
        query: { moduleKey: this.slug, $limit: 1 },
      });
      if (seq !== this.loadSeq) return; // a newer slug superseded this load
      const rows = rowsOf<ModuleCatalogRecord>(result as FindResult<ModuleCatalogRecord>);
      runInAction(() => {
        this.entry = rows[0] ?? null;
        this.notFound = !this.entry;
      });

      // Fan out — each child records its own outcome in sectionErrors.
      void this.loadSummary();
      void this.loadAdoption();
      void this.loadOwners();
      void this.loadGovernance();
      void this.loadReleases();
      void this.loadActivity();
      void this.loadActionItems();
      void this.loadComms();
      void this.loadDevOps();
      void this.loadAssignments();
      void this.loadUsers();
    } catch (err) {
      // Signed out under this call: what the reset left is the next person's.
      if (isSessionEnded(err)) return;
      if (seq !== this.loadSeq) return;
      console.error("Failed to load the module catalog entry:", err);
      runInAction(() => {
        this.notFound = false;
        this.loadError = loadErrorMessage(err, "The catalog entry could not be loaded.");
      });
    } finally {
      if (seq === this.loadSeq) {
        runInAction(() => {
          this.loading = false;
        });
      }
    }
  }

  /** Alias for the header Refresh button / route re-entry / page Retry. */
  async reload() {
    await this.loadModule();
  }

  /**
   * The per-entry rollup. NOTE the query contract: the derived service takes
   * only `governedSlug` (additionalProperties:false) — a `$limit` would 400.
   */
  async loadSummary() {
    if (!this.slug) return;
    await this.runSection(
      "summary",
      "module summary",
      () => findOrThrow(this.moduleSummaryService, { query: { governedSlug: this.slug } }),
      (result) => {
        const rows = rowsOf<ModuleSummaryRecord>(result as FindResult<ModuleSummaryRecord>);
        this.summary = rows.find((row) => row.governedSlug === this.slug) ?? rows[0] ?? null;
      },
    );
  }

  /**
   * Which CLIENTS use this entry, across `client_modules`, `client_tools`
   * (internal only) and `client_integrations`. The service resolves each free
   * text key through the catalog and answers with a COMPOSITE
   * `{ rows, totals, truncated }` object, not a `{ data, total }` page.
   */
  async loadAdoption() {
    if (!this.slug) return;
    runInAction(() => {
      this.adoptionLoading = true;
    });
    try {
      await this.runSection(
        "adoption",
        "module adoption",
        () => findOrThrow(this.moduleAdoptionService, { query: { governedSlug: this.slug } }),
        (raw) => {
          const result = raw as ModuleAdoptionResult | ModuleAdoptionRow[] | null | undefined;
          if (Array.isArray(result)) {
            // Tolerate a bare-array serialisation rather than blanking the tab.
            this.adoptionRows = result;
            this.adoptionTotals = null;
            this.adoptionTruncated = false;
          } else {
            this.adoptionRows = result?.rows ?? [];
            this.adoptionTotals = result?.totals ?? null;
            this.adoptionTruncated = !!result?.truncated;
          }
        },
      );
    } finally {
      runInAction(() => {
        this.adoptionLoading = false;
      });
    }
  }

  async loadOwners() {
    if (!this.slug) return;
    runInAction(() => {
      this.ownersLoading = true;
    });
    try {
      await this.runSection(
        "owners",
        "module owners",
        () =>
          findOrThrow(this.moduleOwnersService, {
            query: {
              governedSlug: this.slug,
              $limit: MODULE_DETAIL_FETCH_LIMIT,
              $sort: { createdAt: 1 },
            },
          }),
        (result) => {
          this.owners = rowsOf<ModuleOwnerRecord>(result as FindResult<ModuleOwnerRecord>);
        },
      );
    } finally {
      runInAction(() => {
        this.ownersLoading = false;
      });
    }
  }

  /**
   * The user directory backing the owner picker + the id → name column.
   * DELIBERATELY TOLERANT: it has no panel of its own and no empty sentence to
   * lie with — a failure degrades the owner names to `#id` and the picker to an
   * empty list, both visibly incomplete rather than falsely "empty".
   */
  async loadUsers() {
    try {
      await this.usersService?.find({ query: { $limit: 500 } });
    } catch (err) {
      console.error("Failed to load the user directory:", err);
    }
  }

  /** Governance facts — also the source of the Data Flow tab's seams. */
  async loadGovernance() {
    if (!this.slug) return;
    runInAction(() => {
      this.governanceLoading = true;
    });
    try {
      await this.runSection(
        "governance",
        "module governance facts",
        () =>
          findOrThrow(this.moduleGovernanceService, {
            query: {
              governedSlug: this.slug,
              $limit: 500,
              $sort: { factType: 1, sortOrder: 1 },
            },
          }),
        (result) => {
          this.governance = rowsOf<ModuleGovernanceFact>(
            result as FindResult<ModuleGovernanceFact>,
          );
        },
      );
    } finally {
      runInAction(() => {
        this.governanceLoading = false;
      });
    }
  }

  async loadReleases() {
    if (!this.slug) return;
    runInAction(() => {
      this.releasesLoading = true;
    });
    try {
      await this.runSection(
        "releases",
        "module releases",
        () =>
          findOrThrow(this.moduleReleasesService, {
            query: {
              governedSlug: this.slug,
              $limit: 500,
              $sort: { observedAt: -1 },
            },
          }),
        (result) => {
          this.releases = rowsOf<ModuleReleaseRecord>(result as FindResult<ModuleReleaseRecord>);
        },
      );
    } finally {
      runInAction(() => {
        this.releasesLoading = false;
      });
    }
  }

  /**
   * The merged timeline. Same query contract as the summary — the derived
   * service REQUIRES `governedSlug` and rejects anything else.
   */
  async loadActivity() {
    if (!this.slug) return;
    runInAction(() => {
      this.activityLoading = true;
      this.activityPage = 1;
    });
    try {
      await this.runSection(
        "activity",
        "module activity",
        () => findOrThrow(this.moduleActivityService, { query: { governedSlug: this.slug } }),
        (result) => {
          this.activity = rowsOf<ModuleActivityItem>(result as FindResult<ModuleActivityItem>);
        },
      );
    } finally {
      runInAction(() => {
        this.activityLoading = false;
      });
    }
  }

  /**
   * Meeting-derived action items attributed to this entry.
   *
   * QUERY CONTRACT: `{ governedSlug: <every spelling>, $limit }`. The API takes
   * the spellings it is given and does NO alias resolution of its own (a second
   * resolver beside `module-resolver.ts` would fork governed identity), so the
   * caller composes the set — see {@link actionItemSpellings}. An empty set
   * would mean "match nothing" server-side, which is why this returns early
   * rather than sending one before the catalog entry has resolved.
   *
   * NO COMPONENT SPECIAL CASE, deliberately: an action item is attributed by
   * SLUG, not by repo, so exact-slug matching returns to a component entry only
   * what the resolver already resolved to it. This is unlike the DevOps loader,
   * which skips components because a link is a REPO-level fact.
   *
   * A failure — including one the ServiceStore swallowed into `errorOnFind` —
   * lands in {@link actionItemsError} via `findOrThrow`, so a dead API never
   * renders as "this module has no action items".
   */
  async loadActionItems() {
    const spellings = this.actionItemSpellings;
    if (!this.slug || spellings.length === 0) return;
    runInAction(() => {
      this.actionItemsLoading = true;
    });
    try {
      await this.runSection(
        "actionItems",
        "action items",
        () =>
          findOrThrow(this.actionItemsService, {
            query: { governedSlug: spellings, $limit: 500 },
          }),
        (result) => {
          this.actionItems = rowsOf<ModuleActionItemRecord>(
            result as FindResult<ModuleActionItemRecord>,
          );
        },
      );
    } finally {
      runInAction(() => {
        this.actionItemsLoading = false;
      });
    }
  }

  /** Module-scoped communications (a fleet-wide note carries no clientId). */
  async loadComms() {
    if (!this.slug) return;
    runInAction(() => {
      this.commsLoading = true;
    });
    try {
      await this.runSection(
        "comms",
        "module communications",
        () =>
          findOrThrow(this.communicationsService, {
            query: {
              governedSlug: this.slug,
              $limit: MODULE_DETAIL_FETCH_LIMIT,
              $sort: { occurredAt: -1 },
            },
          }),
        (result) => {
          this.communications = rowsOf<CommunicationRecord>(
            result as FindResult<CommunicationRecord>,
          );
        },
      );
    } finally {
      runInAction(() => {
        this.commsLoading = false;
      });
    }
  }

  /**
   * Linked repos/pipelines/projects and recent DevOps events for this slug.
   *
   * SKIPPED ENTIRELY for a component entry (Coordinator CATALOG.md §3): a
   * DevOps link is a REPO-level fact and belongs to the parent repo. Querying
   * anyway would either return nothing (and read as "no CI") or, worse, return
   * the parent's rows under the component's name. The tab says which it is.
   *
   * The two finds run in parallel and record their outcomes separately.
   */
  async loadDevOps() {
    if (!this.slug) return;
    if (this.isComponent) {
      runInAction(() => {
        this.devopsSkippedForComponent = true;
        this.devopsLinks = [];
        this.devopsEvents = [];
        this.setSectionError("devopsLinks", null);
        this.setSectionError("devopsEvents", null);
      });
      return;
    }
    runInAction(() => {
      this.devopsSkippedForComponent = false;
      this.devopsLoading = true;
    });
    const slug = this.slug;
    try {
      await Promise.all([
        this.runSection(
          "devopsLinks",
          "DevOps links",
          () =>
            findOrThrow(this.devopsLinksService, {
              query: {
                governedSlug: slug,
                $limit: MODULE_DETAIL_FETCH_LIMIT,
                $sort: { createdAt: -1 },
              },
            }),
          (result) => {
            this.devopsLinks = rowsOf<DevOpsLink>(result as FindResult<DevOpsLink>);
          },
        ),
        this.runSection(
          "devopsEvents",
          "DevOps events",
          () =>
            findOrThrow(this.devopsEventsService, {
              query: { governedSlug: slug, $limit: 100 },
            }),
          (result) => {
            this.devopsEvents = rowsOf<DevOpsEventRecord>(
              result as FindResult<DevOpsEventRecord>,
            );
          },
        ),
      ]);
    } finally {
      runInAction(() => {
        this.devopsLoading = false;
      });
    }
  }

  /**
   * Governed work items attributed to this entry. Queried by `governedRepo` —
   * that is the column `read_ai_assignments` carries and indexes, and it is
   * only known once the catalog entry has resolved.
   */
  async loadAssignments() {
    const repo = this.governedRepo;
    if (!repo) {
      runInAction(() => {
        this.assignments = [];
        this.setSectionError("assignments", null);
      });
      return;
    }
    runInAction(() => {
      this.assignmentsLoading = true;
    });
    try {
      await this.runSection(
        "assignments",
        "governed work items",
        () =>
          findOrThrow(this.assignmentsService, {
            query: { governedRepo: repo, $limit: 500 },
          }),
        (result) => {
          this.assignments = rowsOf<ModuleAssignmentRow>(result as FindResult<ModuleAssignmentRow>);
        },
      );
    } finally {
      runInAction(() => {
        this.assignmentsLoading = false;
      });
    }
  }

  // ---- Owner CRUD (Team tab; operator+, raw service) -----------------------

  startCreateOwner() {
    this.editingOwnerId = null;
    this.ownerError = null;
    this.ownerForm = {
      userId: undefined,
      role: "owner",
      // The first owner recorded for an entry is its primary by default.
      isPrimary: this.liveOwners.length === 0,
      notes: "",
      startedAt: null,
    };
    void this.loadUsers();
  }

  startEditOwner(owner: ModuleOwnerRecord) {
    this.editingOwnerId = owner.id;
    this.ownerError = null;
    this.ownerForm = {
      userId: owner.userId,
      role: owner.role,
      isPrimary: !!owner.isPrimary,
      notes: owner.notes || "",
      startedAt: owner.startedAt || null,
    };
    void this.loadUsers();
  }

  setOwnerField(field: keyof ModuleOwnerFormData, value: string | number | boolean | null) {
    this.ownerForm = { ...this.ownerForm, [field]: value };
  }

  clearOwnerActionError() {
    this.ownerActionError = null;
  }

  clearActionItemReopenError() {
    this.actionItemReopenError = null;
  }

  get isOwnerFormValid(): boolean {
    return !!this.ownerForm.userId && !!this.ownerForm.role;
  }

  /**
   * Create or update an owner through the RAW `module-owners` service so a
   * rejected write (duplicate role, lost tier, primary-conflict) surfaces in
   * {@link ownerError} instead of silently resolving undefined.
   */
  async saveOwner(): Promise<boolean> {
    // Re-check the tier inside the mutator — the getter is the render gate, not
    // the write gate, and the API re-enforces regardless.
    if (!this.canWrite || !this.slug || !this.isOwnerFormValid) return false;
    runInAction(() => {
      this.ownerSaving = true;
      this.ownerError = null;
    });
    try {
      const service = this.rawService("module-owners");
      if (!service) throw new Error("Not connected to the API — reload and try again.");

      const payload: Record<string, unknown> = {
        governedSlug: this.slug,
        userId: Number(this.ownerForm.userId),
        role: this.ownerForm.role,
        isPrimary: !!this.ownerForm.isPrimary,
      };
      if (this.ownerForm.notes) payload.notes = this.ownerForm.notes;
      if (this.ownerForm.startedAt) payload.startedAt = this.ownerForm.startedAt;

      if (this.editingOwnerId) {
        // governedSlug is the scope key the API reads off the EXISTING row on a
        // patch — never re-sent, so a patch can't move an owner between entries.
        delete payload.governedSlug;
        await service.patch(this.editingOwnerId, payload);
      } else {
        await service.create(payload);
      }
      await this.loadOwners();
      runInAction(() => {
        this.editingOwnerId = null;
        this.ownerForm = {};
      });
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.ownerError =
            err instanceof Error && err.message ? err.message : "Failed to save the owner";
        }
      });
      console.error("Failed to save module owner:", err);
      return false;
    } finally {
      runInAction(() => {
        this.ownerSaving = false;
      });
    }
  }

  /** Promote an owner to primary — the API hook demotes the previous one. */
  async setOwnerPrimary(owner: ModuleOwnerRecord): Promise<boolean> {
    if (!this.canWrite) return false;
    runInAction(() => {
      this.ownerActionError = null;
    });
    try {
      const service = this.rawService("module-owners");
      if (!service) throw new Error("Not connected to the API — reload and try again.");
      await service.patch(owner.id, { isPrimary: !owner.isPrimary });
      await this.loadOwners();
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.ownerActionError =
            err instanceof Error && err.message ? err.message : "Failed to update the owner";
        }
      });
      console.error("Failed to set the primary module owner:", err);
      return false;
    }
  }

  async removeOwner(id: string): Promise<boolean> {
    if (!this.canWrite) return false;
    runInAction(() => {
      this.ownerActionError = null;
    });
    try {
      const service = this.rawService("module-owners");
      if (!service) throw new Error("Not connected to the API — reload and try again.");
      await service.remove(id);
      await this.loadOwners();
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.ownerActionError =
            err instanceof Error && err.message ? err.message : "Failed to remove the owner";
        }
      });
      console.error("Failed to remove module owner:", err);
      return false;
    }
  }

  // ---- Module-scoped note (Comms tab; operator+, raw service) --------------

  setNoteSubject(value: string) {
    this.noteSubject = value;
  }

  setNoteSummary(value: string) {
    this.noteSummary = value;
  }

  get isNoteValid(): boolean {
    return this.noteSummary.trim().length > 0;
  }

  /**
   * Add a module-scoped note — a `communications` row with a `governedSlug` and
   * NO clientId (the column was made nullable exactly so a fleet-wide note about
   * a module needs no client). Raw service, for the same reason owner writes use
   * one: a note that silently failed to save is worse than no note.
   */
  async addNote(): Promise<boolean> {
    if (!this.canWrite || !this.slug || !this.isNoteValid) return false;
    runInAction(() => {
      this.noteSaving = true;
      this.noteError = null;
    });
    try {
      const service = this.rawService("communications");
      if (!service) throw new Error("Not connected to the API — reload and try again.");
      await service.create({
        governedSlug: this.slug,
        sourceType: "note",
        subject: this.noteSubject.trim() || null,
        summary: this.noteSummary.trim(),
        occurredAt: new Date().toISOString(),
      });
      runInAction(() => {
        this.noteSubject = "";
        this.noteSummary = "";
      });
      await this.loadComms();
      await this.loadActivity();
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.noteError =
            err instanceof Error && err.message ? err.message : "Failed to add the note";
        }
      });
      console.error("Failed to add a module note:", err);
      return false;
    } finally {
      runInAction(() => {
        this.noteSaving = false;
      });
    }
  }

  // ---- Realtime -----------------------------------------------------------

  /**
   * Subscribe the open slug to live modules-area CRUD events (shared refcounted
   * socket — see ../modulesRealtime.ts). Idempotent per store and torn down by
   * the route `onExit`.
   *
   * The catalog SYNC writes through these same services, so a `yarn
   * catalog:sync` run streams into an open page exactly like a human edit.
   */
  subscribeRealtime() {
    if (this.realtimeDisposer) return;
    this.realtimeDisposer = subscribeModuleEvents(
      (event) => this.handleRealtimeEvent(event),
      this.rootStore,
    );
  }

  /** Route onExit — tear down the live subscription. */
  disposeRealtime() {
    if (this.realtimeDisposer) {
      this.realtimeDisposer();
      this.realtimeDisposer = null;
    }
  }

  /**
   * Apply a live CRUD event SCOPED to the open slug. `module-catalog` events
   * merge into the open entry; the sync-owned collections re-pull the slice
   * they touched (a sync run rewrites whole fact sets, so a per-row merge would
   * leave retired rows behind).
   */
  handleRealtimeEvent(event: ModuleServiceEvent) {
    if (!this.slug) return;
    const record = event.record;

    if (event.service === "module-catalog") {
      if (String(record.moduleKey ?? "") !== this.slug) return;
      runInAction(() => {
        if (event.action === "removed") {
          this.entry = null;
          this.notFound = true;
          return;
        }
        this.entry = { ...(this.entry ?? {}), ...record } as unknown as ModuleCatalogRecord;
        this.notFound = false;
      });
      void this.loadSummary();
      return;
    }

    // Every other module-scoped service carries governedSlug.
    if (String(record.governedSlug ?? "") !== this.slug) return;

    if (event.service === "module-owners") {
      void this.loadOwners();
    } else if (event.service === "module-governance") {
      void this.loadGovernance();
    } else if (event.service === "module-releases") {
      void this.loadReleases();
    }
    void this.loadSummary();
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.ownerActionError = null;
    this.actionItemReopenError = null;
    this.disposeRealtime();
    this.slug = null;
    this.loading = false;
    this.notFound = false;
    this.loadError = null;
    this.sectionErrors = {};
    this.entry = null;
    this.summary = null;
    this.adoptionRows = [];
    this.adoptionTotals = null;
    this.adoptionTruncated = false;
    this.adoptionLoading = false;
    this.owners = [];
    this.ownersLoading = false;
    this.ownerForm = {};
    this.editingOwnerId = null;
    this.ownerSaving = false;
    this.ownerError = null;
    this.assignments = [];
    this.assignmentsLoading = false;
    this.actionItems = [];
    this.actionItemsLoading = false;
    this.governance = [];
    this.governanceLoading = false;
    this.releases = [];
    this.releasesLoading = false;
    this.activity = [];
    this.activityLoading = false;
    this.activityPage = 1;
    this.communications = [];
    this.commsLoading = false;
    this.noteSubject = "";
    this.noteSummary = "";
    this.noteSaving = false;
    this.noteError = null;
    this.devopsLinks = [];
    this.devopsEvents = [];
    this.devopsLoading = false;
    this.devopsSkippedForComponent = false;
    this.adoptionSearch = "";
    this.adoptionSortKey = "client";
    this.adoptionSortDir = "asc";
    this.ownersSortKey = "primary";
    this.ownersSortDir = "desc";
    this.releasesSearch = "";
    this.releasesSortKey = "observedAt";
    this.releasesSortDir = "desc";
    this.governanceSearch = "";
    this.governanceFactTypeFilter = "all";
    this.actionItemsSearch = "";
    this.actionItemsSortKey = "raised";
    this.actionItemsSortDir = "desc";
    this.actionItemsShowClosed = false;
    this.closingActionItemId = null;
    this.closeReason = "done";
    this.closeEvidence = "";
    this.closeDuplicateOfItemId = "";
    this.closingActionItem = false;
    this.actionItemCloseError = null;
    this.reopeningActionItemId = null;
    this.realtimeDisposer = null;
    this.loadSeq += 1;
  }
}

export default ModuleDetailStore;
